import { readImageFileAsDataUrl } from "@/character/frame";
import { slotLabelTh } from "@/home/economy";
import { homeIcon } from "@/home/home-icons";
import { HOME_PART_KINDS } from "@/home/part-kind";
import {
  GM_SHOP_CURRENCY_LABELS,
  GM_SHOP_FULFILLMENT_EDITOR_ORDER,
  GM_SHOP_FULFILLMENT_LABELS,
  GM_SHOP_PLACEHOLDER_IMAGE,
  createGmShopItemTemplate,
  deleteGmShopFromCatalog,
  gmShopEffectivePrice,
  gmShopListPrice,
  hydrateGmShopCatalog,
  isStorageQuotaError,
  loadGmShopCatalog,
  type GmShopCurrency,
  type GmShopFulfillmentKind,
  type GmShopItem,
  upsertGmShopCatalog,
} from "@/gm/gm-shop-catalog";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

type ShopTab = "all" | GmShopCurrency;

let draftImageUrl: string | null = null;
let editingId: string | null = null;
let activeTab: ShopTab = "all";

type FormState = Omit<GmShopItem, "version" | "createdAt" | "updatedAt">;

function defaultForm(): FormState {
  return {
    id: "",
    name: "",
    description: "",
    imageDataUrl: GM_SHOP_PLACEHOLDER_IMAGE,
    currency: "coins",
    price: 100,
    discountPercent: 0,
    saleEnabled: true,
    fulfillment: "health_potion_small",
    catalogPartKind: "prop",
    catalogSlotsGranted: 5,
    sortOrder: 100,
  };
}

let formState: FormState = defaultForm();

function previewImageUrl(): string {
  return draftImageUrl ?? formState.imageDataUrl;
}

function itemsForTab(tab: ShopTab): GmShopItem[] {
  const all = loadGmShopCatalog();
  if (tab === "all") return all;
  return all.filter((item) => item.currency === tab);
}

function priceLabel(item: GmShopItem): string {
  const list = gmShopListPrice(item);
  const effective = gmShopEffectivePrice(item);
  const cur = GM_SHOP_CURRENCY_LABELS[item.currency];
  if (item.discountPercent > 0 && effective < list) {
    return `<span class="gm-shop-price-was">${list.toLocaleString("en-US")}</span> <strong>${effective.toLocaleString("en-US")}</strong> ${escapeHtml(cur)} (−${item.discountPercent}%)`;
  }
  return `<strong>${effective.toLocaleString("en-US")}</strong> ${escapeHtml(cur)}`;
}

function renderShopCard(item: GmShopItem): string {
  const salePillClass = item.saleEnabled
    ? "gm-shop-sale-pill gm-shop-sale-pill--on"
    : "gm-shop-sale-pill gm-shop-sale-pill--off";
  const salePillText = item.saleEnabled ? "ขาย" : "ปิด";
  const salePillTitle = item.saleEnabled ? "เปิดขายอยู่" : "ปิดการขายอยู่";
  const toggleLabel = item.saleEnabled ? "ปิดการขาย" : "เปิดการขาย";
  const toggleIcon = item.saleEnabled ? homeIcon("close") : homeIcon("shop");
  return `
    <article class="gm-monster-card gm-shop-card" role="listitem" data-shop-id="${escapeHtml(item.id)}">
      <div class="gm-monster-card-art-wrap">
        <img class="gm-monster-card-art" src="${escapeHtml(item.imageDataUrl)}" alt="" width="72" height="72" loading="lazy" />
        <span class="gm-monster-card-badge gm-shop-currency-badge">${escapeHtml(GM_SHOP_CURRENCY_LABELS[item.currency])}</span>
      </div>
      <div class="gm-monster-card-body">
        <h3 class="gm-monster-card-title">${escapeHtml(item.name)}</h3>
        <p class="gm-monster-card-stats hint">${escapeHtml(GM_SHOP_FULFILLMENT_LABELS[item.fulfillment])}</p>
        <p class="gm-shop-card-price hint">${priceLabel(item)}</p>
        ${item.description ? `<p class="gm-shop-card-desc hint">${escapeHtml(item.description)}</p>` : ""}
        <p class="gm-shop-card-meta hint">ลำดับ ${item.sortOrder}</p>
      </div>
      <footer class="gm-shop-card-toolbar">
        <span class="${salePillClass}" title="${escapeHtml(salePillTitle)}">${salePillText}</span>
        <div class="gm-shop-card-toolbar-btns" role="group" aria-label="จัดการสินค้า">
          <button type="button" class="gm-icon-tool gm-shop-toggle-sale" data-id="${escapeHtml(item.id)}" aria-label="${escapeHtml(toggleLabel)}" title="${escapeHtml(toggleLabel)}">${toggleIcon}</button>
          <button type="button" class="gm-icon-tool gm-shop-edit" data-id="${escapeHtml(item.id)}" aria-label="แก้ไข" title="แก้ไข">${homeIcon("decorate")}</button>
          <button type="button" class="gm-icon-tool gm-icon-tool--danger gm-shop-delete" data-id="${escapeHtml(item.id)}" aria-label="ลบ" title="ลบ">${homeIcon("trash")}</button>
        </div>
      </footer>
    </article>`;
}

function renderCatalogBody(): string {
  const items = itemsForTab(activeTab);
  if (items.length === 0) {
    return `
      <div class="gm-monster-empty">
        <p>ยังไม่มีสินค้าในหมวดนี้</p>
        <p class="hint">กด <strong>+</strong> เพื่อเพิ่มไอเทมร้าน</p>
      </div>
    `;
  }
  return `<div class="gm-monster-card-grid" role="list">${items.map(renderShopCard).join("")}</div>`;
}

function renderTabs(): string {
  const tabs: { id: ShopTab; label: string }[] = [
    { id: "all", label: "ทั้งหมด" },
    { id: "coins", label: "ทอง" },
    { id: "gems", label: "เพชร" },
    { id: "cash", label: "Cash" },
  ];
  return tabs
    .map((tab) => {
      const active = tab.id === activeTab ? " gm-parts-tab--active" : "";
      return `<button type="button" class="gm-parts-tab${active}" data-shop-tab="${tab.id}">${escapeHtml(tab.label)}</button>`;
    })
    .join("");
}

function fulfillmentOptions(selected: GmShopFulfillmentKind): string {
  const normalized =
    selected === "health_potion" ? "health_potion_small" : selected;
  return GM_SHOP_FULFILLMENT_EDITOR_ORDER.map((key) => {
      const sel = key === normalized ? " selected" : "";
      return `<option value="${key}"${sel}>${escapeHtml(GM_SHOP_FULFILLMENT_LABELS[key])}</option>`;
    })
    .join("");
}

function currencyOptions(selected: GmShopCurrency): string {
  return (["coins", "gems", "cash"] as GmShopCurrency[])
    .map((key) => {
      const sel = key === selected ? " selected" : "";
      return `<option value="${key}"${sel}>${escapeHtml(GM_SHOP_CURRENCY_LABELS[key])}</option>`;
    })
    .join("");
}

function kindOptions(selected: string): string {
  return HOME_PART_KINDS.map((kind) => {
    const sel = kind === selected ? " selected" : "";
    return `<option value="${kind}"${sel}>${escapeHtml(slotLabelTh(kind))}</option>`;
  }).join("");
}

function renderEditForm(): string {
  const f = formState;
  const img = previewImageUrl();
  const showCatalog = f.fulfillment === "catalog_slot_pack";
  return `
    <form class="gm-monster-form" id="gm-shop-edit-form">
      <section class="gm-monster-form-section" aria-labelledby="gm-shop-sec-image">
        <h3 class="gm-monster-form-section-title" id="gm-shop-sec-image">รูปไอเทม</h3>
        <div class="gm-monster-upload-zone">
          <label class="gm-monster-upload-label">
            <span class="gm-monster-upload-icon" aria-hidden="true">↑</span>
            <span class="gm-monster-upload-text">อัปโหลดรูป</span>
            <input type="file" id="gm-shop-image-file" accept="image/png,image/jpeg,image/webp" hidden />
          </label>
          <div class="gm-monster-preview-slot">
            <img class="gm-part-form-preview" src="${escapeHtml(img)}" alt="" width="120" height="120" />
          </div>
        </div>
      </section>

      <section class="gm-monster-form-section" aria-labelledby="gm-shop-sec-info">
        <h3 class="gm-monster-form-section-title" id="gm-shop-sec-info">รายละเอียด</h3>
        <label class="field gm-monster-field">
          <span class="field-label">ชื่อสินค้า</span>
          <input type="text" class="input" id="gm-shop-name" value="${escapeHtml(f.name)}" maxlength="80" required />
        </label>
        <label class="field gm-monster-field">
          <span class="field-label">คำอธิบาย</span>
          <textarea class="input gm-shop-desc-input" id="gm-shop-description" rows="3" maxlength="400">${escapeHtml(f.description)}</textarea>
        </label>
        <label class="field gm-monster-field">
          <span class="field-label">ผลเมื่อซื้อ</span>
          <select class="input" id="gm-shop-fulfillment">${fulfillmentOptions(f.fulfillment)}</select>
        </label>
        <div class="gm-monster-grid gm-monster-grid--footprint">
          <label class="field gm-monster-field">
            <span class="field-label">สกุลเงิน</span>
            <select class="input" id="gm-shop-currency">${currencyOptions(f.currency)}</select>
          </label>
          <label class="field gm-monster-field">
            <span class="field-label">ราคาปกติ</span>
            <input type="number" class="input" id="gm-shop-price" min="0" step="1" value="${f.price}" />
          </label>
          <label class="field gm-monster-field">
            <span class="field-label">ลดราคา (%)</span>
            <input type="number" class="input" id="gm-shop-discount" min="0" max="90" step="1" value="${f.discountPercent}" />
          </label>
          <label class="field gm-monster-field">
            <span class="field-label">ลำดับแสดง</span>
            <input type="number" class="input" id="gm-shop-sort" min="0" max="9999" step="1" value="${f.sortOrder}" />
          </label>
        </div>
        <label class="gm-tower-enabled-field">
          <input type="checkbox" id="gm-shop-sale-enabled" ${f.saleEnabled ? "checked" : ""} />
          <span>เปิดขายในร้านผู้เล่น</span>
        </label>
        <p class="hint" id="gm-shop-price-preview">ราคาขายจริง: <strong>—</strong></p>
        <div id="gm-shop-catalog-fields" ${showCatalog ? "" : "hidden"}>
          <label class="field gm-monster-field">
            <span class="field-label">หมวดคลัง (ค่าเริ่ม)</span>
            <select class="input" id="gm-shop-catalog-kind">${kindOptions(f.catalogPartKind ?? "prop")}</select>
          </label>
          <label class="field gm-monster-field">
            <span class="field-label">จำนวนช่องต่อแพ็ก</span>
            <input type="number" class="input" id="gm-shop-catalog-slots" min="1" max="50" value="${f.catalogSlotsGranted ?? 5}" />
          </label>
        </div>
      </section>

      <p class="hint gm-monster-form-status" id="gm-shop-status" role="status"></p>
      <div class="gm-monster-modal-foot">
        <button type="button" class="btn btn-ghost" id="gm-shop-cancel-edit">ยกเลิก</button>
        <button type="submit" class="btn btn-primary">บันทึก</button>
      </div>
    </form>
  `;
}

function modalTitle(): string {
  return editingId ? "แก้ไขสินค้าร้าน" : "เพิ่มสินค้าร้าน";
}

export function renderGmShopSectionHtml(): string {
  const count = loadGmShopCatalog().length;
  return `
    <p class="hint gm-settings-lead">
      จัดการไอเทมร้าน (ทอง / เพชร / Cash) · ยาเพิ่มเลือด 3 แบบ (+100 / +500 / +1000) · sync ไป <a href="/home">ร้านในบ้าน</a>
    </p>
    <div class="gm-monsters-page gm-shop-page">
      <header class="gm-monsters-toolbar">
        <div class="gm-monsters-toolbar-text">
          <h2 class="gm-monsters-toolbar-title">สินค้าร้าน</h2>
          <p class="hint" id="gm-shop-catalog-count">${count} รายการ</p>
        </div>
        <button type="button" class="gm-monster-fab" id="gm-shop-open-create" aria-label="เพิ่มสินค้า">
          <span aria-hidden="true">+</span>
        </button>
      </header>
      <nav class="gm-parts-tabs" aria-label="สกุลเงิน">${renderTabs()}</nav>
      <div id="gm-shop-list-host">${renderCatalogBody()}</div>
      <p class="hint gm-monster-toast" id="gm-shop-toast" role="status"></p>
    </div>

    <div id="gm-shop-modal" class="gm-monster-modal" hidden>
      <button type="button" class="gm-monster-modal-backdrop" id="gm-shop-modal-backdrop" aria-label="ปิด"></button>
      <div class="gm-monster-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="gm-shop-modal-title">
        <header class="gm-monster-modal-head">
          <h2 class="gm-monster-modal-title" id="gm-shop-modal-title">${modalTitle()}</h2>
          <button type="button" class="btn btn-ghost btn-sm gm-monster-modal-close" id="gm-shop-modal-close" aria-label="ปิด">✕</button>
        </header>
        <div class="gm-monster-modal-body" id="gm-shop-modal-body"></div>
      </div>
    </div>
  `;
}

function readEditForm(root: HTMLElement): FormState | null {
  const name = root.querySelector<HTMLInputElement>("#gm-shop-name")?.value.trim();
  if (!name) return null;
  const num = (id: string, fallback = 0) =>
    Math.floor(Number(root.querySelector<HTMLInputElement>(`#${id}`)?.value ?? fallback));
  const fulfillment = root.querySelector<HTMLSelectElement>("#gm-shop-fulfillment")
    ?.value as GmShopFulfillmentKind;
  const currency = root.querySelector<HTMLSelectElement>("#gm-shop-currency")
    ?.value as GmShopCurrency;
  return {
    ...formState,
    id: editingId ?? "",
    name,
    description: root.querySelector<HTMLTextAreaElement>("#gm-shop-description")?.value.trim() ?? "",
    imageDataUrl: previewImageUrl(),
    currency: currency ?? "coins",
    price: num("gm-shop-price", 0),
    discountPercent: Math.min(90, Math.max(0, num("gm-shop-discount", 0))),
    saleEnabled: root.querySelector<HTMLInputElement>("#gm-shop-sale-enabled")?.checked ?? true,
    fulfillment: fulfillment ?? "health_potion_small",
    catalogPartKind: root.querySelector<HTMLSelectElement>("#gm-shop-catalog-kind")?.value as FormState["catalogPartKind"],
    catalogSlotsGranted: num("gm-shop-catalog-slots", 5),
    sortOrder: num("gm-shop-sort", 100),
  };
}

function refreshCatalog(root: HTMLElement): void {
  const host = root.querySelector<HTMLElement>("#gm-shop-list-host");
  const countEl = root.querySelector<HTMLElement>("#gm-shop-catalog-count");
  const tabs = root.querySelector<HTMLElement>(".gm-parts-tabs");
  if (host) host.innerHTML = renderCatalogBody();
  if (countEl) countEl.textContent = `${loadGmShopCatalog().length} รายการ`;
  if (tabs) tabs.innerHTML = renderTabs();
}

function setStatus(root: HTMLElement, msg: string): void {
  const el = root.querySelector<HTMLElement>("#gm-shop-status");
  if (el) el.textContent = msg;
}

function setToast(root: HTMLElement, msg: string): void {
  const el = root.querySelector<HTMLElement>("#gm-shop-toast");
  if (el) el.textContent = msg;
}

function updatePricePreview(root: HTMLElement): void {
  const preview = root.querySelector<HTMLElement>("#gm-shop-price-preview strong");
  if (!preview) return;
  const draft = readEditForm(root);
  if (!draft) {
    preview.textContent = "—";
    return;
  }
  const item = createGmShopItemTemplate(draft);
  preview.textContent = `${gmShopEffectivePrice(item).toLocaleString("en-US")} ${GM_SHOP_CURRENCY_LABELS[item.currency]}`;
}

export async function prepareGmShopSection(): Promise<void> {
  await hydrateGmShopCatalog();
}

export function bindGmShopSection(root: HTMLElement): () => void {
  const closeModal = () => {
    const modal = root.querySelector<HTMLElement>("#gm-shop-modal");
    if (modal) modal.hidden = true;
    document.body.classList.remove("gm-monster-modal-open");
  };

  const openEditModal = (item?: GmShopItem) => {
    if (item) {
      editingId = item.id;
      formState = { ...item };
      draftImageUrl = null;
    } else {
      editingId = null;
      formState = defaultForm();
      draftImageUrl = null;
    }
    const modal = root.querySelector<HTMLElement>("#gm-shop-modal");
    const body = root.querySelector<HTMLElement>("#gm-shop-modal-body");
    const title = root.querySelector<HTMLElement>("#gm-shop-modal-title");
    if (title) title.textContent = modalTitle();
    if (body) body.innerHTML = renderEditForm();
    if (modal) modal.hidden = false;
    document.body.classList.add("gm-monster-modal-open");
    updatePricePreview(root);
  };

  const onRootClick = async (event: Event) => {
    const target = event.target as HTMLElement;
    const tabBtn = target.closest<HTMLElement>("[data-shop-tab]");
    if (tabBtn?.dataset.shopTab) {
      activeTab = tabBtn.dataset.shopTab as ShopTab;
      refreshCatalog(root);
      return;
    }
    const editBtn = target.closest<HTMLButtonElement>(".gm-shop-edit");
    if (editBtn?.dataset.id) {
      const item = loadGmShopCatalog().find((e) => e.id === editBtn.dataset.id);
      if (item) openEditModal(item);
      return;
    }
    const delBtn = target.closest<HTMLButtonElement>(".gm-shop-delete");
    if (delBtn?.dataset.id) {
      if (!window.confirm("ลบสินค้านี้จากร้าน?")) return;
      try {
        await deleteGmShopFromCatalog(delBtn.dataset.id);
        refreshCatalog(root);
        setToast(root, "ลบแล้ว");
      } catch (err) {
        setToast(root, isStorageQuotaError(err) ? "พื้นที่เก็บข้อมูลเต็ม" : "ลบไม่สำเร็จ");
      }
      return;
    }
    if (target.closest("#gm-shop-cancel-edit")) {
      closeModal();
      return;
    }
    const toggleBtn = target.closest<HTMLButtonElement>(".gm-shop-toggle-sale");
    if (toggleBtn?.dataset.id) {
      const item = loadGmShopCatalog().find((e) => e.id === toggleBtn.dataset.id);
      if (!item) return;
      try {
        await upsertGmShopCatalog({ ...item, saleEnabled: !item.saleEnabled });
        refreshCatalog(root);
        setToast(root, !item.saleEnabled ? "เปิดการขายแล้ว" : "ปิดการขายแล้ว");
      } catch (err) {
        setToast(root, isStorageQuotaError(err) ? "พื้นที่เก็บข้อมูลเต็ม" : "บันทึกไม่สำเร็จ");
      }
    }
  };

  const onFormInput = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id === "gm-shop-fulfillment") {
      const show =
        (target as HTMLSelectElement).value === "catalog_slot_pack";
      root.querySelector<HTMLElement>("#gm-shop-catalog-fields")?.toggleAttribute("hidden", !show);
    }
    if (
      target.matches(
        "#gm-shop-price, #gm-shop-discount, #gm-shop-currency, #gm-shop-fulfillment",
      )
    ) {
      updatePricePreview(root);
    }
  };

  const onSubmit = async (event: Event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.id !== "gm-shop-edit-form") {
      return;
    }
    event.preventDefault();
    const draft = readEditForm(root);
    if (!draft) {
      setStatus(root, "กรอกชื่อสินค้า");
      return;
    }
    const existing = editingId ? loadGmShopCatalog().find((e) => e.id === editingId) : undefined;
    const entry = createGmShopItemTemplate({
      ...draft,
      id: editingId ?? undefined,
    });
    if (existing) entry.createdAt = existing.createdAt;
    try {
      await upsertGmShopCatalog(entry);
      closeModal();
      editingId = null;
      formState = defaultForm();
      draftImageUrl = null;
      refreshCatalog(root);
      setToast(root, `บันทึก «${entry.name}» แล้ว`);
    } catch (err) {
      setStatus(root, isStorageQuotaError(err) ? "พื้นที่เก็บข้อมูลเต็ม" : "บันทึกไม่สำเร็จ");
    }
  };

  const onFile = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.id !== "gm-shop-image-file" || !input.files?.[0]) return;
    try {
      draftImageUrl = await readImageFileAsDataUrl(input.files[0]);
      const img = root.querySelector<HTMLImageElement>(".gm-part-form-preview");
      if (img) img.src = draftImageUrl;
      input.value = "";
    } catch {
      setStatus(root, "อัปโหลดรูปไม่สำเร็จ");
    }
  };

  root.addEventListener("click", onRootClick);
  root.addEventListener("input", onFormInput);
  root.addEventListener("change", onFile);
  root.addEventListener("submit", onSubmit);

  root.querySelector("#gm-shop-open-create")?.addEventListener("click", () => openEditModal());
  root.querySelector("#gm-shop-modal-backdrop")?.addEventListener("click", closeModal);
  root.querySelector("#gm-shop-modal-close")?.addEventListener("click", closeModal);
  return () => {
    root.removeEventListener("click", onRootClick);
    root.removeEventListener("input", onFormInput);
    root.removeEventListener("change", onFile);
    root.removeEventListener("submit", onSubmit);
    closeModal();
  };
}
